import React, { useEffect, useState, useCallback, useContext } from "react";
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, CircularProgress, Alert, Switch,
  FormControlLabel, Tooltip, Snackbar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import api from "../../utils/api";
import { PermissionsContext } from "../../utils/PermissionsContext";

/* ── Shared styles ──────────────────────────────────────────────────────── */
const primaryBtnSx = {
  backgroundColor: "#2f63ad", color: "#fff", fontWeight: 700, borderRadius: "10px",
  textTransform: "none", boxShadow: "none",
  "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
};
const secondaryBtnSx = { borderRadius: "10px", textTransform: "none", fontWeight: 600 };
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fff" } };
const dialogPaperSx = { borderRadius: "18px", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" };

/* ── Marina CRUD dialog ─────────────────────────────────────────────────── */
const BLANK = {
  name: "", address: "", city: "", state: "", zip: "",
  phone: "", email: "", description: "", latitude: "", longitude: "", is_active: true,
};

const MarinaDialog = ({ open, editing, onClose, onSaved }) => {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm(editing
        ? { ...BLANK, ...editing, is_active: Boolean(editing.is_active ?? 1) }
        : BLANK);
      setErr("");
    }
  }, [open, editing]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) { setErr("Marina name is required."); return; }
    setSaving(true);
    try {
      const payload = { ...form, is_active: form.is_active ? 1 : 0 };
      if (editing?.id) await api.put(`/marinas/${editing.id}`, payload);
      else             await api.post("/marinas", payload);
      onSaved();
      onClose();
    } catch { setErr("Failed to save. Please try again."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ fontWeight: 700, pt: 3, pb: 1 }}>
        {editing ? "Edit Marina" : "Add Marina"}
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        {err && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{err}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField fullWidth label="Marina Name *" value={form.name} onChange={set("name")} sx={fieldSx} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Address" value={form.address} onChange={set("address")} sx={fieldSx} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="City" value={form.city} onChange={set("city")} sx={fieldSx} />
          </Grid>
          <Grid item xs={3}>
            <TextField fullWidth label="State" value={form.state} onChange={set("state")} sx={fieldSx} />
          </Grid>
          <Grid item xs={3}>
            <TextField fullWidth label="Zip" value={form.zip} onChange={set("zip")} sx={fieldSx} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Phone" value={form.phone} onChange={set("phone")} sx={fieldSx} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Email" value={form.email} onChange={set("email")} sx={fieldSx} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={2} label="Description" value={form.description} onChange={set("description")} sx={fieldSx} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Latitude" type="number" value={form.latitude} onChange={set("latitude")} sx={fieldSx}
              helperText="For map centering" />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Longitude" type="number" value={form.longitude} onChange={set("longitude")} sx={fieldSx}
              helperText="For map centering" />
          </Grid>
          {editing && (
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} color="success" />}
                label="Active"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1.25 }}>
        <Button onClick={onClose} variant="outlined" sx={secondaryBtnSx} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" sx={primaryBtnSx} disabled={saving}>
          {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : editing ? "Update Marina" : "Add Marina"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Main component ─────────────────────────────────────────────────────── */
const MarinaSettings = () => {
  const perms      = useContext(PermissionsContext);
  const canManage  = perms.includes("marina.manage_slips");

  const [marinas,  setMarinas]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [dialog,   setDialog]   = useState({ open: false, editing: null });
  const [deleting, setDeleting] = useState(null);
  const [snack,    setSnack]    = useState({ open: false, msg: "", sev: "success" });

  const showSnack = (msg, sev = "success") => setSnack({ open: true, msg, sev });

  const fetchMarinas = useCallback(async () => {
    try {
      const res = await api.get("/marinas");
      setMarinas(res.data || []);
    } catch { showSnack("Failed to load marinas", "error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMarinas(); }, [fetchMarinas]);

  const handleDelete = async (marina) => {
    if (!window.confirm(`Delete "${marina.name}"? This will remove all associated docks, slips, and reservations.`)) return;
    setDeleting(marina.id);
    try {
      await api.delete(`/marinas/${marina.id}`);
      showSnack("Marina deleted");
      fetchMarinas();
    } catch { showSnack("Failed to delete marina", "error"); }
    finally { setDeleting(null); }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
            Marina Settings
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Add and manage marinas. Docks, slips, reservations, and vessels are configured inside each marina from the Marina module.
          </Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} sx={primaryBtnSx}
            onClick={() => setDialog({ open: true, editing: null })}>
            Add Marina
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : marinas.length === 0 ? (
        <Paper elevation={0} sx={{
          borderRadius: "16px", border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 4px 14px rgba(0,0,0,0.04)", p: 5, textAlign: "center",
        }}>
          <DirectionsBoatIcon sx={{ fontSize: 52, color: "rgba(47,99,173,0.2)", mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
            No Marinas Yet
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c", mb: 3 }}>
            Add a marina to get started managing docks, slips, and reservations.
          </Typography>
          {canManage && (
            <Button variant="contained" startIcon={<AddIcon />} sx={primaryBtnSx}
              onClick={() => setDialog({ open: true, editing: null })}>
              Add First Marina
            </Button>
          )}
        </Paper>
      ) : (
        <Paper elevation={0} sx={{
          borderRadius: "16px", border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 4px 14px rgba(0,0,0,0.04)", overflow: "hidden",
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  {["Marina", "Location", "Contact", "Slips", "Status", ""].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#5f6b7a", py: 1.5 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {marinas.map((m) => (
                  <TableRow key={m.id} hover sx={{ "&:last-child td": { border: 0 } }}>

                    {/* Name + description */}
                    <TableCell sx={{ py: 1.75 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <Box sx={{
                          width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
                          backgroundColor: "rgba(47,99,173,0.1)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <DirectionsBoatIcon sx={{ color: "#2f63ad", fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142" }}>
                            {m.name}
                          </Typography>
                          {m.description && (
                            <Typography variant="caption" sx={{ color: "#9aa5b4", display: "block",
                              maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {m.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Location */}
                    <TableCell sx={{ fontSize: "0.82rem", color: "#445" }}>
                      {[m.city, m.state].filter(Boolean).join(", ") || "N/A"}
                      {m.address && (
                        <Typography variant="caption" sx={{ color: "#9aa5b4", display: "block" }}>
                          {m.address}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Contact */}
                    <TableCell sx={{ fontSize: "0.82rem", color: "#445" }}>
                      {m.phone || m.email ? (
                        <>
                          {m.phone && <Typography variant="caption" sx={{ display: "block" }}>{m.phone}</Typography>}
                          {m.email && <Typography variant="caption" sx={{ color: "#9aa5b4", display: "block" }}>{m.email}</Typography>}
                        </>
                      ) : "N/A"}
                    </TableCell>

                    {/* Slip summary */}
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                        {m.slip_count != null && (
                          <Chip label={`${m.slip_count} total`} size="small"
                            sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600, backgroundColor: "rgba(47,99,173,0.08)", color: "#2f63ad" }} />
                        )}
                        {m.available_slips != null && (
                          <Chip label={`${m.available_slips} avail.`} size="small"
                            sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600, backgroundColor: "rgba(22,163,74,0.1)", color: "#15803d" }} />
                        )}
                        {m.slip_count == null && <Typography variant="caption" sx={{ color: "#9aa5b4" }}>-</Typography>}
                      </Box>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Chip
                        label={m.is_active ? "Active" : "Inactive"}
                        size="small"
                        sx={{
                          height: 22, fontSize: "0.72rem", fontWeight: 700,
                          backgroundColor: m.is_active ? "rgba(22,163,74,0.1)" : "rgba(107,119,140,0.1)",
                          color: m.is_active ? "#15803d" : "#6b7280",
                        }}
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right">
                      {canManage && (
                        <>
                          <Tooltip title="Edit marina">
                            <IconButton size="small" sx={{ color: "#2f63ad" }}
                              onClick={() => setDialog({ open: true, editing: m })}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete marina">
                            <IconButton size="small" color="error"
                              disabled={deleting === m.id}
                              onClick={() => handleDelete(m)}>
                              {deleting === m.id
                                ? <CircularProgress size={16} />
                                : <DeleteIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <MarinaDialog
        open={dialog.open}
        editing={dialog.editing}
        onClose={() => setDialog({ open: false, editing: null })}
        onSaved={() => {
          fetchMarinas();
          showSnack(dialog.editing ? "Marina updated" : "Marina added");
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MarinaSettings;
